// src/pages/AdminSetup.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { getAdminDebugInfo, verifyAdminUser } from '../utils/adminUtils';
import { setupCurrentUserAsAdmin, getAdminSystemStatus, runPlanMigration, checkProblematicUser } from '../utils/adminSetup';

const AdminSetup = () => {
  const [adminInfo, setAdminInfo] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [problematicUserEmail, setProblematicUserEmail] = useState('');
  const [problematicUserResult, setProblematicUserResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadAdminInfo();
  }, []);

  const loadAdminInfo = async () => {
    try {
      setLoading(true);
      const info = await getAdminDebugInfo();
      setAdminInfo(info);
      
      const status = await getAdminSystemStatus();
      setSystemStatus(status);
    } catch (error) {
      console.error('Error loading admin info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupAdmin = async () => {
    try {
      setActionLoading(true);
      const result = await setupCurrentUserAsAdmin();
      if (result) {
        alert('✅ Admin setup completed! Please refresh the page.');
        window.location.reload();
      } else {
        alert('❌ Failed to setup admin. Check console for details.');
      }
    } catch (error) {
      console.error('Setup admin error:', error);
      alert('❌ Error during admin setup');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyAdmin = async () => {
    try {
      setActionLoading(true);
      const result = await verifyAdminUser();
      if (result) {
        alert('✅ Admin user verified/fixed! Please refresh the page.');
        window.location.reload();
      } else {
        alert('❌ Failed to verify admin user. Check console for details.');
      }
    } catch (error) {
      console.error('Verify admin error:', error);
      alert('❌ Error during admin verification');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunPlanMigration = async () => {
    try {
      setActionLoading(true);
      setMigrationResult(null);
      
      const result = await runPlanMigration();
      setMigrationResult(result);
      
      if (result.success) {
        alert(`✅ Plan migration completed!\n\n📊 Results:\n- ${result.migratedCount} users migrated\n- ${result.skippedCount} users skipped\n- ${result.errorCount} errors`);
      } else {
        alert(`❌ Plan migration failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Plan migration error:', error);
      alert('❌ Error during plan migration');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckProblematicUser = async () => {
    if (!problematicUserEmail.trim()) {
      alert('Please enter an email address to check');
      return;
    }

    try {
      setActionLoading(true);
      setProblematicUserResult(null);
      
      const result = await checkProblematicUser(problematicUserEmail.trim());
      setProblematicUserResult(result);
      
      console.log('Problematic user check result:', result);
      
      if (result.recommendations && result.recommendations.length > 0) {
        alert(`User Check Results:\n\n${result.recommendations.join('\n')}`);
      }
    } catch (error) {
      console.error('Problematic user check error:', error);
      alert('❌ Error checking user');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading admin setup...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin Setup & Debug</h1>
          <p className="text-gray-300">Configure and troubleshoot admin access</p>
        </div>

        {/* Admin Status */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Admin Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">Current Status</h3>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                adminInfo?.isAdmin ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
              }`}>
                {adminInfo?.isAdmin ? '✅ Admin Access Granted' : '❌ Admin Access Denied'}
              </div>
              {adminInfo?.reason && (
                <p className="text-sm text-gray-400 mt-2">{adminInfo.reason}</p>
              )}
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-white mb-2">User Info</h3>
              <div className="space-y-1 text-sm text-gray-300">
                <p><strong>UID:</strong> {adminInfo?.userId || 'Not available'}</p>
                <p><strong>Email:</strong> {adminInfo?.userEmail || 'Not available'}</p>
                <p><strong>Role:</strong> {adminInfo?.userRole || 'Not set'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Actions */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Admin Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={handleSetupAdmin}
              disabled={actionLoading}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Setting up...' : '🔧 Setup Current User as Admin'}
            </button>
            
            <button
              onClick={handleVerifyAdmin}
              disabled={actionLoading}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Verifying...' : '🔍 Verify Admin User'}
            </button>
            
            <button
              onClick={handleRunPlanMigration}
              disabled={actionLoading}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Migrating...' : '🔄 Run Plan Migration'}
            </button>
          </div>
          
          {/* Migration Results */}
          {migrationResult && (
            <div className="mt-4 p-4 bg-gray-700 rounded-lg">
              <h3 className="text-lg font-medium text-white mb-2">Migration Results</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{migrationResult.migratedCount}</p>
                  <p className="text-gray-400">Migrated</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">{migrationResult.skippedCount}</p>
                  <p className="text-gray-400">Skipped</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{migrationResult.errorCount}</p>
                  <p className="text-gray-400">Errors</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Problematic User Check */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8 border border-gray-700">
          <h2 className="text-xl font-semibold text-white mb-4">Check Problematic User</h2>
          <p className="text-gray-300 mb-4">Check if a user exists in Firebase Auth or Firestore and get cleanup recommendations.</p>
          
          <div className="flex gap-4 mb-4">
            <input
              type="email"
              placeholder="Enter email address to check"
              value={problematicUserEmail}
              onChange={(e) => setProblematicUserEmail(e.target.value)}
              className="flex-1 p-3 rounded bg-gray-900 text-white border border-gray-700 focus:border-green-500 focus:outline-none"
            />
            <button
              onClick={handleCheckProblematicUser}
              disabled={actionLoading || !problematicUserEmail.trim()}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {actionLoading ? 'Checking...' : '🔍 Check User'}
            </button>
          </div>
          
          {/* Problematic User Results */}
          {problematicUserResult && (
            <div className="mt-4 p-4 bg-gray-700 rounded-lg">
              <h3 className="text-lg font-medium text-white mb-2">User Check Results</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Email:</span>
                  <span className="text-white">{problematicUserResult.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Firebase Auth:</span>
                  <span className={problematicUserResult.authExists ? 'text-red-400' : 'text-green-400'}>
                    {problematicUserResult.authExists ? 'Exists' : 'Not Found'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Firestore:</span>
                  <span className={problematicUserResult.firestoreExists ? 'text-red-400' : 'text-green-400'}>
                    {problematicUserResult.firestoreExists ? 'Exists' : 'Not Found'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Available for Signup:</span>
                  <span className={problematicUserResult.authAvailable ? 'text-green-400' : 'text-red-400'}>
                    {problematicUserResult.authAvailable ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              
              {problematicUserResult.recommendations && problematicUserResult.recommendations.length > 0 && (
                <div className="mt-4 p-3 bg-gray-600 rounded-lg">
                  <h4 className="text-yellow-400 font-medium mb-2">Recommendations:</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    {problematicUserResult.recommendations.map((rec, index) => (
                      <li key={index}>• {rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* System Status */}
        {systemStatus && (
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-8 border border-gray-700">
            <h2 className="text-xl font-semibold text-white mb-4">System Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-2">Database</h3>
                <div className={`inline-flex items-center px-2 py-1 rounded text-sm ${
                  systemStatus.databaseConnection ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                }`}>
                  {systemStatus.databaseConnection ? '✅ Connected' : '❌ Disconnected'}
                </div>
              </div>
              
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-2">Users</h3>
                <p className="text-2xl font-bold text-white">{systemStatus.userCount}</p>
                <p className="text-sm text-gray-400">Total users</p>
              </div>
              
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-medium text-white mb-2">Games</h3>
                <p className="text-2xl font-bold text-white">{systemStatus.gameCount}</p>
                <p className="text-sm text-gray-400">Total games</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={() => navigate('/admin')}
            className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
          >
            ← Back to Dashboard
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            🔄 Refresh Page
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup; 